import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { AccountGroupType } from '@model/AccountGroupType'
import { TradingAccountStatusType } from '@model/TradingAccount'

export const useTradingPoolCampaignResults = () => {
  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tradingPoolCampResults.setPageQuery({
      ...tradingPoolCampResults.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()

  const maskAccountName = (value: string) =>
    value.replace(/^(.{5}).+$/, (_, firstFive) => {
      return firstFive + '*'.repeat(value.length - 5)
    })

  const { campaignId } = useParams<{ accountId: string; campaignId: string }>()

  const tableQuery = usePaginatedQuery({
    queryKey: queryKeys.tradingAccount.live(),
    queryFn: (q) =>
      apiClient.getTradingAccounts({
        ...q,
        search: {
          AccountGroupType: { value: AccountGroupType.Live, operator: Operator.EQUAL },
          Status: { value: TradingAccountStatusType.Approved, operator: Operator.EQUAL },
        },
      }),
  })

  const tradingAccountNames = tableQuery.data.map((ta) => ta.name)

  const tradingPoolCampResults = usePaginatedQuery({
    queryKey: queryKeys.campaign.tradingPoolResults(campaignId),
    queryFn: (q) =>
      apiClient.getTradingPoolCampaignResults(campaignId!, {
        ...q,
      }),
    enabled: !!campaignId,
  })

  const {
    data: tradingPoolCampaignDetails,
    isLoading: tradingPoolCampaignDetailsLoading,
    error: tradingPoolCampaignDetailsError,
  } = useQuery({
    queryKey: queryKeys.campaign.tradingPoolDetail(campaignId, locale),
    queryFn: () => apiClient.getTradingPoolCampaign(campaignId!, locale),
    enabled: !!campaignId,
  })

  const progress = tradingPoolCampaignDetails?.clientResult?.progressToNextEntry ?? 0
  const lotsPerEntry = tradingPoolCampaignDetails?.clientResult?.lotsPerEntry ?? 0

  const percentage = lotsPerEntry > 0 ? (progress / lotsPerEntry) * 100 : 0

  return {
    maskAccountName,
    percentage,
    tradingPoolCampResults,
    handlePageChange,
    tradingAccountNames,
    tradingPoolCampaignResults: tradingPoolCampResults.data,
    tradingPoolCampaignDetails,
    tradingPoolCampaignResultsLoading: tradingPoolCampResults.isLoading,
    tradingPoolCampaignDetailsLoading,
    tradingPoolCampaignDetailsError,
  }
}
